/**
 * VistoeraTASK-00068 — Media Picker sidebar count (facet) cache.
 *
 * Module-scoped (survives Picker close/reopen within the same tab, but
 * never persisted to disk/localStorage — cleared on full page reload).
 * Purely a "stale-while-revalidate" read-through cache: reopening the
 * Picker shows the last-known count for that exact workspace/site scope
 * immediately while a fresh `GET /summary` request revalidates it in the
 * background. Never shown across a different workspace/site (the cache
 * key always includes both).
 */
import type { ImagePickerSummaryResponse } from '../../api'

export interface PickerCountCacheEntry {
  total: number
  summary: ImagePickerSummaryResponse['summary']
  cachedAtMs: number
}

const _cache = new Map<string, PickerCountCacheEntry>()

export function pickerCountCacheKey(
  workspaceId: string,
  siteId: string | null | undefined,
  includeLibrary: boolean,
): string {
  return `${workspaceId}|${siteId || ''}|${includeLibrary ? 1 : 0}`
}

export function getCachedPickerCount(key: string): PickerCountCacheEntry | null {
  return _cache.get(key) ?? null
}

export function setCachedPickerCount(
  key: string,
  value: { total: number; summary: ImagePickerSummaryResponse['summary'] },
): void {
  _cache.set(key, { ...value, cachedAtMs: Date.now() })
}

/** Test/dev-only reset — never called from product code paths. */
export function _resetPickerCountCacheForTests(): void {
  _cache.clear()
}

/**
 * VistoeraTASK-00249 §26 — a delete performed on the Image Assets Center
 * page (a *different* surface than this Picker) must not leave this
 * module-scoped cache holding a stale (higher) count. Without this, a
 * tenant who deletes assets on the Center page and then opens the Canvas
 * Picker for the SAME workspace would briefly see the pre-delete total/
 * summary again (the exact "flash back" bug this task explicitly forbids)
 * until the picker's own background revalidation fetch overwrites it —
 * this makes that stale window zero instead of one paint.
 *
 * Clears every cache entry for the workspace regardless of site/
 * includeLibrary scope (this cache's key always starts with
 * ``${workspaceId}|`` — see `pickerCountCacheKey`) since a delete's
 * effect on total/summary is workspace-wide, not scoped to one site.
 */
export function invalidateCachedPickerCount(workspaceId: string): void {
  const prefix = `${workspaceId}|`
  for (const key of _cache.keys()) {
    if (key.startsWith(prefix)) _cache.delete(key)
  }
}
